import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Package, MagnifyingGlass, Camera, Trash, CheckCircle, Check, X,
  LockSimple, ArrowClockwise, ShieldCheck, Boat, FilePdf, CaretLeft, CaretRight, Stack,
} from "@phosphor-icons/react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import "./ContainerLoading.css";

const PHOTO_PATH = (id) => `/logistics/container-loading/photos/${id}`;

// Mandatory stacking-layer options for each loading photo (keep in sync with backend layer_label).
// Layers are NOT capped at 6 — a container can be stacked higher. Ordinal words up to 20, then "Layer N".
const LAYER_ORDINALS = ["First", "Second", "Third", "Fourth", "Fifth", "Sixth", "Seventh", "Eighth",
  "Ninth", "Tenth", "Eleventh", "Twelfth", "Thirteenth", "Fourteenth", "Fifteenth", "Sixteenth",
  "Seventeenth", "Eighteenth", "Nineteenth", "Twentieth"];
const MAX_LAYERS = 60;
const layerLabel = (n) =>
  (n >= 1 && n <= MAX_LAYERS) ? (n <= LAYER_ORDINALS.length ? `${LAYER_ORDINALS[n - 1]} layer` : `Layer ${n}`) : "";
// Dropdown options for manual (single-layer) uploads. Bulk auto-upload is not bound by this list.
const LAYERS = Array.from({ length: LAYER_ORDINALS.length }, (_, i) => ({ v: i + 1, label: layerLabel(i + 1) }));

// ── Client-side image compressor: downscale + JPEG quality search to land under maxBytes,
// keeping the picture clearly legible (never below ~0.4 quality / ~900px). ──
function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}
async function compressImage(file, maxBytes = 150 * 1024) {
  if (!file.type.startsWith("image/")) throw new Error("Not an image file");
  const img = await loadImage(file);
  const render = (maxDim, q) => {
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    return new Promise((res) => canvas.toBlob(res, "image/jpeg", q));
  };
  for (const maxDim of [1600, 1280, 1024, 900]) {
    for (let q = 0.9; q >= 0.4; q -= 0.12) {
      const blob = await render(maxDim, q);
      if (blob && blob.size <= maxBytes) {
        const name = (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
        return new File([blob], name, { type: "image/jpeg" });
      }
    }
  }
  const blob = await render(900, 0.4);
  return new File([blob], "photo.jpg", { type: "image/jpeg" });
}

// Auth-protected <img>: the photo endpoint needs a Bearer token, so fetch the bytes as a blob.
function AuthImg({ id, onClick }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let alive = true; let obj;
    api.get(PHOTO_PATH(id), { responseType: "blob" })
      .then((r) => { if (!alive) return; obj = URL.createObjectURL(r.data); setUrl(obj); })
      .catch(() => {});
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [id]);
  return url
    ? <img src={url} alt="container load" onClick={onClick} data-testid={`cl-photo-${id}`} />
    : <div className="cl-img-skel" />;
}

export default function ContainerLoading() {
  const { user } = useAuth();
  const canEdit = ["logistics", "admin", "super_admin"].includes(user?.role);

  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loadingList, setLoadingList] = useState(false);
  const [sel, setSel] = useState(null);           // selected container_no
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lbIdx, setLbIdx] = useState(null);        // index into orderedPhotos (null = closed)
  const [layerByInv, setLayerByInv] = useState({}); // invoice_no -> selected layer (number) for next upload
  const fileRefs = useRef({});                      // invoice_no -> <input> (single-layer add)
  const bulkRefs = useRef({});                      // invoice_no -> <input> (bulk auto-layer upload)
  const checkFileRefs = useRef({});                 // checklist point key -> <input>

  const loadList = useCallback(() => {
    setLoadingList(true);
    api.get("/logistics/container-loading/containers", { params: { q, status: statusFilter, limit: 80 } })
      .then((r) => { setList(r.data.containers || []); setTotal(r.data.total || 0); })
      .catch(() => toast.error("Failed to load containers"))
      .finally(() => setLoadingList(false));
  }, [q, statusFilter]);

  useEffect(() => { const t = setTimeout(loadList, 250); return () => clearTimeout(t); }, [loadList]);

  const loadDetail = useCallback((cno) => {
    if (!cno) return;
    setLoadingDetail(true);
    api.get(`/logistics/container-loading/containers/${encodeURIComponent(cno)}`)
      .then((r) => setDetail(r.data))
      .catch(() => toast.error("Failed to load container"))
      .finally(() => setLoadingDetail(false));
  }, []);

  const openContainer = (cno) => { setSel(cno); setDetail(null); loadDetail(cno); };

  const refreshBoth = (d) => { if (d) setDetail(d); loadList(); };

  const onFiles = async (invNo, fileList, auto = false) => {
    if (!canEdit || !sel) return;
    const layer = layerByInv[invNo];
    if (!auto && !layer) { toast.error("Select which layer these photos show first"); return; }
    let files = Array.from(fileList || []);
    if (!files.length) return;
    if (auto) {
      // Bulk upload: order files lowest→highest by filename so the FIRST photo becomes the First
      // layer, the next the Second layer, and so on — one layer per photo, with NO cap (the 7th
      // photo is the Seventh layer, etc.).
      files = files.slice().sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, { numeric: true, sensitivity: "base" }));
    }
    setBusy(true);
    const tid = toast.loading(`Compressing & uploading ${files.length} photo(s)…`);
    try {
      let lastDetail = null;
      let idx = 0;
      for (const f of files) {
        if (!f.type.startsWith("image/")) { toast.error(`${f.name}: not an image`); continue; }
        const small = await compressImage(f);
        const useLayer = auto ? Math.min(idx + 1, MAX_LAYERS) : layer;
        const fd = new FormData();
        fd.append("invoice_no", invNo);
        fd.append("layer", String(useLayer));
        fd.append("files", small, small.name);
        const r = await api.post(
          `/logistics/container-loading/containers/${encodeURIComponent(sel)}/photos`, fd);
        lastDetail = r.data.detail;
        idx += 1;
      }
      refreshBoth(lastDetail);
      toast.success(auto ? `Uploaded ${idx} photo(s) · layered First → ${layerLabel(Math.min(idx, MAX_LAYERS))}`
                         : "Photos uploaded", { id: tid });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed", { id: tid });
    } finally {
      setBusy(false);
      if (fileRefs.current[invNo]) fileRefs.current[invNo].value = "";
      if (bulkRefs.current[invNo]) bulkRefs.current[invNo].value = "";
    }
  };

  const deletePhoto = async (fileId) => {
    if (!canEdit || !sel) return;
    try {
      const r = await api.delete(
        `/logistics/container-loading/containers/${encodeURIComponent(sel)}/photos/${fileId}`);
      refreshBoth(r.data.detail);
      toast.success("Photo removed");
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const onCheckFiles = async (pointKey, fileList) => {
    if (!canEdit || !sel) return;
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    const tid = toast.loading(`Uploading inspection photo(s)…`);
    try {
      let lastDetail = null;
      for (const f of files) {
        if (!f.type.startsWith("image/")) { toast.error(`${f.name}: not an image`); continue; }
        const small = await compressImage(f);
        const fd = new FormData();
        fd.append("files", small, small.name);
        const r = await api.post(
          `/logistics/container-loading/containers/${encodeURIComponent(sel)}/checklist/${pointKey}/photos`, fd);
        lastDetail = r.data.detail;
      }
      refreshBoth(lastDetail);
      toast.success("Inspection photo added", { id: tid });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed", { id: tid });
    } finally {
      setBusy(false);
      if (checkFileRefs.current[pointKey]) checkFileRefs.current[pointKey].value = "";
    }
  };

  const toggleCheck = async (p) => {
    if (!canEdit || !detail) return;
    // A point can't be ticked OK without an inspection photo — prompt to add one first.
    if (!p.checked && (p.photo_count || 0) === 0) {
      toast.message("Add an inspection photo first", { description: `${p.label} needs at least one photo before it can be marked OK.` });
      checkFileRefs.current[p.key]?.click();
      return;
    }
    const points = {};
    detail.checklist.forEach((x) => { points[x.key] = x.key === p.key ? !x.checked : x.checked; });
    try {
      const r = await api.put(
        `/logistics/container-loading/containers/${encodeURIComponent(sel)}/checklist`, { points });
      refreshBoth(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update checklist"); }
  };

  const setComplete = async (completed) => {
    if (!canEdit || !sel) return;
    setBusy(true);
    try {
      const r = await api.put(
        `/logistics/container-loading/containers/${encodeURIComponent(sel)}/status`, { completed });
      refreshBoth(r.data);
      toast.success(completed ? "Marked Loading Complete" : "Reopened for edits");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    if (!sel) return;
    const tid = toast.loading("Building PDF pack…");
    try {
      const r = await api.get(
        `/logistics/container-loading/containers/${encodeURIComponent(sel)}/pdf`,
        { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("PDF ready", { id: tid });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not build PDF", { id: tid });
    }
  };

  const photosByInv = (invNo) => (detail?.photos || []).filter((p) => p.invoice_no === invNo);

  // Flat, display-order list of every photo on this container (loading photos grouped by invoice,
  // then C-TPAT inspection photos) so the lightbox can step through them with ←/→.
  const orderedPhotos = useMemo(() => {
    if (!detail) return [];
    const out = [];
    (detail.invoices || []).forEach((iv) => {
      (detail.photos || []).filter((p) => p.invoice_no === iv.inv_no).forEach((p) => out.push(p));
    });
    (detail.checklist || []).forEach((pt) => (pt.photos || []).forEach((ph) => out.push(ph)));
    return out;
  }, [detail]);

  const openLightbox = (id) => {
    const i = orderedPhotos.findIndex((p) => p.id === id);
    setLbIdx(i >= 0 ? i : 0);
  };

  // Keyboard navigation for the lightbox: ←/→ to swipe, Esc to close.
  useEffect(() => {
    if (lbIdx === null) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") setLbIdx((i) => Math.max(0, (i ?? 0) - 1));
      else if (e.key === "ArrowRight") setLbIdx((i) => Math.min(orderedPhotos.length - 1, (i ?? 0) + 1));
      else if (e.key === "Escape") setLbIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lbIdx, orderedPhotos.length]);

  const curPhoto = lbIdx !== null ? orderedPhotos[lbIdx] : null;

  return (
    <Layout breadcrumb={["Logistics", "Container Loading Photos"]}>
      <div style={{ padding: "16px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "var(--omh-text)", display: "flex", alignItems: "center", gap: 9 }}>
              <Package size={22} weight="duotone" /> Container Loading Photos
            </div>
            <div style={{ fontSize: 12.5, color: "var(--omh-text-muted)", marginTop: 3 }}>
              FCL export containers · each needs <b>8+ loading photos</b> and the <b>C-TPAT 7-point inspection</b> (a photo per point) completed
            </div>
          </div>
          {!canEdit && (
            <span className="cl-ro" data-testid="cl-readonly-badge">
              <LockSimple size={14} weight="bold" /> Read-only · only Logistics or Admins can upload or edit
            </span>
          )}
        </div>

        <div className="cl-wrap">
          {/* ── Container list ── */}
          <div className="cl-panel cl-list" data-testid="cl-container-list">
            <div className="cl-list-h">
              <div style={{ position: "relative" }}>
                <MagnifyingGlass size={15} style={{ position: "absolute", left: 10, top: 10, color: "var(--omh-text-soft)" }} />
                <input className="cl-search" style={{ paddingLeft: 30 }} placeholder="Search container / invoice / customer"
                  value={q} onChange={(e) => setQ(e.target.value)} data-testid="cl-search" />
              </div>
              <div className="cl-chips">
                {[["", "All"], ["not_started", "Not started"], ["in_progress", "In progress"], ["complete", "Complete"]].map(([v, l]) => (
                  <span key={v} className={`cl-chip ${statusFilter === v ? "active" : ""}`}
                    onClick={() => setStatusFilter(v)} data-testid={`cl-filter-${v || "all"}`}>{l}</span>
                ))}
              </div>
              <div style={{ fontSize: 11, color: "var(--omh-text-soft)", marginTop: 8 }}>
                {loadingList ? "Loading…" : `${list.length} shown · ${total} total`}
              </div>
            </div>
            {list.map((c) => (
              <div key={c.container_no} className={`cl-card ${sel === c.container_no ? "sel" : ""}`}
                onClick={() => openContainer(c.container_no)} data-testid={`cl-card-${c.container_no}`}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <span className="cl-cno">{c.container_no}</span>
                  <span className={`cl-badge ${c.status}`}>
                    {c.status === "complete" ? "Complete" : c.status === "in_progress" ? "In progress" : "Not started"}
                  </span>
                </div>
                <div className="cl-sub">
                  {c.invoice_count} invoice{c.invoice_count === 1 ? "" : "s"}
                  {c.parties?.length ? ` · ${c.parties.join(", ")}` : ""}
                  {c.cont_size ? ` · ${c.cont_size}` : ""}
                </div>
                <div className="cl-meter">
                  <span><b className={c.photo_count >= c.min_photos ? "" : ""}>{c.photo_count}</b>/{c.min_photos} photos</span>
                  <span><b>{c.checklist_done}</b>/{c.checklist_total} checks</span>
                </div>
              </div>
            ))}
            {!loadingList && !list.length && <div className="cl-empty">No containers match.</div>}
          </div>

          {/* ── Detail ── */}
          <div className="cl-panel">
            {!sel ? (
              <div className="cl-blank" data-testid="cl-blank">
                <Package size={40} weight="duotone" style={{ opacity: .4 }} />
                <div style={{ marginTop: 10, fontSize: 14 }}>Select a container to view and add loading photos.</div>
              </div>
            ) : loadingDetail || !detail ? (
              <div className="cl-blank">Loading container…</div>
            ) : (
              <div className="cl-detail" data-testid="cl-detail">
                <div className="cl-dhead">
                  <div>
                    <div className="cl-dtitle" data-testid="cl-detail-title">{detail.container_no}</div>
                    <div className="cl-dmeta">
                      {detail.meta.shipping_line && <span><Boat size={13} weight="bold" style={{ verticalAlign: -2 }} /> <b>{detail.meta.shipping_line}</b></span>}
                      {detail.meta.cont_size && <span>Size <b>{detail.meta.cont_size}</b></span>}
                      {detail.meta.booking_no && <span>Booking <b>{detail.meta.booking_no}</b></span>}
                      {detail.meta.bl_no && <span>B/L <b>{detail.meta.bl_no}</b></span>}
                      <span>{detail.invoice_count} invoice{detail.invoice_count === 1 ? "" : "s"}</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <button className="cl-btn" onClick={downloadPdf} data-testid="cl-pdf-btn"
                      title="Download photos + signed C-TPAT checklist as a single PDF">
                      <FilePdf size={15} weight="bold" /> PDF Pack
                    </button>
                    {detail.completed ? (
                      <>
                        <span className="cl-badge complete" style={{ fontSize: 11.5 }}><CheckCircle size={14} weight="fill" /> Loading complete</span>
                        {canEdit && <button className="cl-btn" disabled={busy} onClick={() => setComplete(false)} data-testid="cl-reopen-btn">
                          <ArrowClockwise size={14} /> Reopen</button>}
                      </>
                    ) : canEdit ? (
                      <button className="cl-btn success" disabled={busy || !detail.requirements_met}
                        onClick={() => setComplete(true)} data-testid="cl-complete-btn"
                        title={detail.requirements_met ? "Mark this container's loading as complete"
                          : `Need ${detail.min_photos}+ photos and all ${detail.checklist_total} checks first`}>
                        <CheckCircle size={15} weight="bold" /> Mark Loading Complete
                      </button>
                    ) : null}
                  </div>
                </div>

                {/* Requirement tiles */}
                <div className="cl-req">
                  <div className="cl-req-tile" data-testid="cl-req-photos">
                    <div className={`n ${detail.photo_count >= detail.min_photos ? "ok" : "bad"}`}>
                      {detail.photo_count}<span style={{ fontSize: 14, color: "var(--omh-text-soft)" }}> / {detail.min_photos}</span>
                    </div>
                    <div className="l">Loading photos</div>
                  </div>
                  <div className="cl-req-tile" data-testid="cl-req-checklist">
                    <div className={`n ${detail.checklist_ready === detail.checklist_total ? "ok" : "bad"}`}>
                      {detail.checklist_ready}<span style={{ fontSize: 14, color: "var(--omh-text-soft)" }}> / {detail.checklist_total}</span>
                    </div>
                    <div className="l">C-TPAT checks</div>
                  </div>
                  <div className="cl-req-tile">
                    <div className={`n ${detail.requirements_met ? "ok" : "bad"}`}>{detail.requirements_met ? "Ready" : "Pending"}</div>
                    <div className="l">Status</div>
                  </div>
                </div>

                {/* Invoices + photos */}
                <div className="cl-sec-title"><Camera size={14} weight="bold" /> Loading photos by invoice</div>
                {detail.invoices.map((iv) => {
                  const photos = photosByInv(iv.inv_no);
                  return (
                    <div className="cl-inv" key={iv.inv_no} data-testid={`cl-invoice-${iv.inv_no}`}>
                      <div className="cl-inv-h">
                        <div>
                          <span className="cl-inv-no">Invoice {iv.inv_no}</span>
                          <span className="cl-inv-party"> · {iv.party || "—"}{iv.currency ? ` · ${iv.currency}` : ""}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 11.5, color: "var(--omh-text-muted)" }}>{photos.length} photo{photos.length === 1 ? "" : "s"}</span>
                          {canEdit && (
                            <>
                              <select className="cl-layer-sel" data-testid={`cl-layer-${iv.inv_no}`}
                                value={layerByInv[iv.inv_no] || ""}
                                onChange={(e) => setLayerByInv((m) => ({ ...m, [iv.inv_no]: e.target.value ? Number(e.target.value) : undefined }))}
                                title="Select which layer the photos you're about to add show">
                                <option value="">Select layer…</option>
                                {LAYERS.map((l) => <option key={l.v} value={l.v}>{l.label}</option>)}
                              </select>
                              <button className="cl-btn" disabled={busy || !layerByInv[iv.inv_no]} data-testid={`cl-add-${iv.inv_no}`}
                                title={layerByInv[iv.inv_no] ? "" : "Pick a layer first"}
                                onClick={() => fileRefs.current[iv.inv_no]?.click()}>
                                <Camera size={14} weight="bold" /> Add photos
                              </button>
                              <input ref={(el) => (fileRefs.current[iv.inv_no] = el)} type="file" accept="image/*" multiple
                                style={{ display: "none" }} onChange={(e) => onFiles(iv.inv_no, e.target.files)} />
                              <button className="cl-btn primary" disabled={busy} data-testid={`cl-bulk-${iv.inv_no}`}
                                title="Upload many photos at once — layers are assigned automatically, lowest filename = First layer, then Second, Third…"
                                onClick={() => bulkRefs.current[iv.inv_no]?.click()}>
                                <Stack size={14} weight="bold" /> Bulk upload (auto layers)
                              </button>
                              <input ref={(el) => (bulkRefs.current[iv.inv_no] = el)} type="file" accept="image/*" multiple
                                data-testid={`cl-bulk-input-${iv.inv_no}`}
                                style={{ display: "none" }} onChange={(e) => onFiles(iv.inv_no, e.target.files, true)} />
                            </>
                          )}
                        </div>
                      </div>
                      {photos.length ? (
                        <div className="cl-grid">
                          {photos.map((p) => (
                            <div className="cl-thumb-wrap" key={p.id}>
                              <div className="cl-thumb">
                                <AuthImg id={p.id} onClick={() => openLightbox(p.id)} />
                                <span className="cl-layer-badge" data-testid={`cl-layer-badge-${p.id}`}>
                                  <Stack size={11} weight="bold" /> {p.layer_label || "No layer"}
                                </span>
                              </div>
                              {canEdit && (
                                <button className="cl-del-foot" title="Remove this photo" data-testid={`cl-del-${p.id}`}
                                  onClick={() => deletePhoto(p.id)}>
                                  <Trash size={12} weight="bold" /> Delete
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="cl-empty">No photos yet for this invoice.</div>
                      )}
                    </div>
                  );
                })}

                {/* C-TPAT checklist */}
                <div className="cl-sec-title"><ShieldCheck size={14} weight="bold" /> C-TPAT 7-Point Container Inspection</div>
                <div className="cl-check" data-testid="cl-checklist">
                  {detail.checklist.map((p) => (
                    <div key={p.key} className={`cl-check-row ${p.ready ? "on" : ""}`}
                      data-testid={`cl-check-${p.key}`} style={{ alignItems: "flex-start" }}>
                      <div className="cl-check-box" data-testid={`cl-check-box-${p.key}`}
                        onClick={() => toggleCheck(p)}
                        style={{ cursor: canEdit ? "pointer" : "default", marginTop: 1 }}>
                        {p.checked && <Check size={14} weight="bold" />}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div className="cl-check-lbl">{p.label}</div>
                        {p.note && <div className="cl-check-n">{p.note}</div>}
                        {/* per-point inspection photos */}
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6, alignItems: "center" }}>
                          {(p.photos || []).map((ph) => (
                            <div key={ph.id} className="cl-pt-thumb" style={{ position: "relative", width: 52, height: 40, borderRadius: 6, overflow: "hidden", border: "1px solid var(--omh-border)" }}>
                              <AuthImg id={ph.id} onClick={() => openLightbox(ph.id)} />
                              {canEdit && (
                                <button className="cl-del" data-testid={`cl-del-${ph.id}`} title="Remove"
                                  onClick={(e) => { e.stopPropagation(); deletePhoto(ph.id); }}
                                  style={{ width: 18, height: 18 }}><Trash size={10} weight="bold" /></button>
                              )}
                            </div>
                          ))}
                          {canEdit && (
                            <>
                              <button className="cl-btn" data-testid={`cl-check-add-${p.key}`}
                                disabled={busy} onClick={() => checkFileRefs.current[p.key]?.click()}
                                style={{ padding: "4px 9px", fontSize: 11.5 }}>
                                <Camera size={12} weight="bold" /> Photo
                              </button>
                              <input ref={(el) => (checkFileRefs.current[p.key] = el)} type="file" accept="image/*" multiple
                                style={{ display: "none" }} onChange={(e) => onCheckFiles(p.key, e.target.files)} />
                            </>
                          )}
                          {!p.photos?.length && !canEdit && <span className="cl-check-n">No photo</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {detail.checklist_by && (
                  <div data-testid="cl-checklist-stamp" style={{ marginTop: 10, fontSize: 11.5, color: "var(--omh-text-soft)" }}>
                    Checklist last updated by <b>{detail.checklist_by}</b>
                    {detail.checklist_at ? ` · ${detail.checklist_at.slice(0, 16).replace("T", " ")} UTC` : ""}
                  </div>
                )}
                {detail.completed && detail.completed_by && (
                  <div style={{ marginTop: 6, fontSize: 11.5, color: "var(--omh-text-soft)" }}>
                    Completed by <b>{detail.completed_by}</b>{detail.completed_at ? ` · ${detail.completed_at.slice(0, 16).replace("T", " ")} UTC` : ""}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {curPhoto && (
        <div className="cl-lightbox" onClick={() => setLbIdx(null)} data-testid="cl-lightbox">
          <button className="cl-lightbox-x" data-testid="cl-lightbox-close" onClick={() => setLbIdx(null)}><X size={18} weight="bold" /></button>
          {lbIdx > 0 && (
            <button className="cl-lightbox-nav left" data-testid="cl-lightbox-prev"
              onClick={(e) => { e.stopPropagation(); setLbIdx((i) => Math.max(0, i - 1)); }}
              title="Previous (←)"><CaretLeft size={24} weight="bold" /></button>
          )}
          <div className="cl-lightbox-stage" onClick={(e) => e.stopPropagation()}>
            <AuthImg id={curPhoto.id} />
            <div className="cl-lightbox-cap" data-testid="cl-lightbox-caption">
              {curPhoto.layer_label ? <span className="cl-layer-badge"><Stack size={12} weight="bold" /> {curPhoto.layer_label}</span> : null}
              <span className="cl-lightbox-count">{lbIdx + 1} / {orderedPhotos.length}</span>
            </div>
          </div>
          {lbIdx < orderedPhotos.length - 1 && (
            <button className="cl-lightbox-nav right" data-testid="cl-lightbox-next"
              onClick={(e) => { e.stopPropagation(); setLbIdx((i) => Math.min(orderedPhotos.length - 1, i + 1)); }}
              title="Next (→)"><CaretRight size={24} weight="bold" /></button>
          )}
        </div>
      )}
    </Layout>
  );
}
